export interface RunningHoursReading {
  /** Okuma zamanı, ISO-8601 UTC. */
  readAt: string;
  /** Sayaçtaki toplam çalışma saati. */
  totalHours: number;
}

export type RunningHoursError =
  | 'INVALID_DATE'
  | 'FUTURE_DATE'
  | 'NEGATIVE_VALUE'
  | 'NOT_AFTER_PREVIOUS_READING';

export type RunningHoursConfirmation = 'LOWER_THAN_PREVIOUS' | 'DAILY_AVERAGE_OVER_24H';

export interface RunningHoursCheck {
  /** Kaydı engelleyen hatalar. */
  errors: RunningHoursError[];
  /** Kullanıcının açıkça onaylaması gereken durumlar. */
  confirmationsRequired: RunningHoursConfirmation[];
}

const HOUR_MS = 3_600_000;

/**
 * Çalışma saati değeri: en fazla bir ondalık (0,1 saat = 6 dakika). Sayaçlar çoğu zaman tam saat
 * okunur; ondalık kabul etmek veri kaybettirmez (ADR 0012 §1).
 */
export function isValidRunningHours(value: unknown): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && Math.round(value * 10) / 10 === value
  );
}

/**
 * NR496 [4.5.1] çalışma saati kontrolleri.
 * `serverNow` gemi sunucusunun saatidir; "gelecek tarih" ona göre belirlenir (mimari belge §6.11).
 */
export function checkRunningHoursReading(
  reading: RunningHoursReading,
  previous: RunningHoursReading | null,
  serverNow: string,
): RunningHoursCheck {
  const errors: RunningHoursError[] = [];
  const confirmationsRequired: RunningHoursConfirmation[] = [];

  const at = Date.parse(reading.readAt);
  const now = Date.parse(serverNow);
  const previousAt = previous ? Date.parse(previous.readAt) : null;
  if (Number.isNaN(at) || Number.isNaN(now) || (previousAt !== null && Number.isNaN(previousAt))) {
    return { errors: ['INVALID_DATE'], confirmationsRequired };
  }

  if (at > now) errors.push('FUTURE_DATE');
  if (reading.totalHours < 0) errors.push('NEGATIVE_VALUE');

  if (previous && previousAt !== null) {
    if (at <= previousAt) {
      errors.push('NOT_AFTER_PREVIOUS_READING');
    } else {
      const delta = reading.totalHours - previous.totalHours;
      const elapsedHours = (at - previousAt) / HOUR_MS;
      if (delta < 0) confirmationsRequired.push('LOWER_THAN_PREVIOUS');
      // Günlük ortalama > 24 saat  <=>  artış > geçen süre (saat)
      if (delta > elapsedHours) confirmationsRequired.push('DAILY_AVERAGE_OVER_24H');
    }
  }

  return { errors, confirmationsRequired };
}
